import type { MouseEvent } from 'react';
import { useState } from 'react';
import useFieldId from 'core/hooks/useFieldId';
import { useTranslate } from 'core/hooks/useTranslate';
import type { AttachmentContent, UploadedFile } from 'app/types/attachment';

type UseAttachmentCardProps = {
  content?: AttachmentContent;
  guid?: string;
  onAddContent?: () => void;
  onDelete?: (guid: string) => void;
  onUploadFiles?: (files: File[]) => void;
  onShowVisor?: (content: AttachmentContent, event: MouseEvent, url: string, type: string) => void;
  onPreviewUploadedFile?: (file: UploadedFile, index: number) => void;
  onDeleteUploadedFile?: (index: number) => void;
  style?: 'link' | 'default';
  uploadedFiles?: UploadedFile[];
};

type UseAttachmentCardReturn = {
  addContentReasonId: string;
  handleAddContent: () => void;
  isAddContentDisabled: boolean;
  isUploadDialogOpen: boolean;
  handleOpenUploadDialog: () => void;
  handleCloseUploadDialog: () => void;
  handleUploadSuccess: (files: File[]) => void;
  handleShowVisor: (event: MouseEvent) => void;
  handleDelete: () => void;
  makeHandlePreviewFile: (file: UploadedFile, index: number) => () => void;
  makeHandleDeleteUploadedFile: (index: number) => () => void;
  itemTitleClass: string;
  hasScrollableList: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
};

function useAttachmentCard({
  content,
  guid,
  onAddContent,
  onDelete,
  onUploadFiles,
  onShowVisor,
  onPreviewUploadedFile,
  onDeleteUploadedFile,
  style = 'default',
  uploadedFiles,
}: UseAttachmentCardProps): UseAttachmentCardReturn {
  const { t } = useTranslate();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const { helperId: addContentReasonId } = useFieldId('attachment-card-add-content');

  const itemTitleClass = `attachment-card__item-title attachment-card__item-title--${style === 'link' ? 'link' : 'file'}`;
  const totalItems = (guid ? 1 : 0) + (uploadedFiles?.length ?? 0);
  const hasScrollableList = totalItems > 5;
  const isAddContentDisabled = Boolean(guid);

  // El botón va con aria-disabled y no con disabled para que siga recibiendo el foco y se lea
  // el motivo; por eso el clic hay que frenarlo aquí.
  function handleAddContent(): void {
    if (!isAddContentDisabled && onAddContent) onAddContent();
  }

  function handleOpenUploadDialog() {
    setIsUploadDialogOpen(true);
  }

  function handleCloseUploadDialog() {
    setIsUploadDialogOpen(false);
  }

  function handleUploadSuccess(files: File[]) {
    if (onUploadFiles) onUploadFiles(files);
    setIsUploadDialogOpen(false);
  }

  function handleShowVisor(event: MouseEvent) {
    if (onShowVisor) onShowVisor(content ?? {}, event, content?.url ?? '', content?.type ?? '');
  }

  function handleDelete() {
    if (onDelete && guid) onDelete(guid);
  }

  function makeHandlePreviewFile(file: UploadedFile, index: number) {
    return function handlePreviewFile() {
      if (onPreviewUploadedFile) onPreviewUploadedFile(file, index);
    };
  }

  function makeHandleDeleteUploadedFile(index: number) {
    return function handleDeleteUploadedFile() {
      if (onDeleteUploadedFile) onDeleteUploadedFile(index);
    };
  }

  return {
    addContentReasonId,
    handleAddContent,
    isAddContentDisabled,
    isUploadDialogOpen,
    handleOpenUploadDialog,
    handleCloseUploadDialog,
    handleUploadSuccess,
    handleShowVisor,
    handleDelete,
    makeHandlePreviewFile,
    makeHandleDeleteUploadedFile,
    itemTitleClass,
    hasScrollableList,
    t,
  };
}

export default useAttachmentCard;
